/**
 * The per-language playground page's SEO, as pure builders (`GR-PLG01`).
 *
 * `docs/24_PLAYGROUND_SPEC.md` §11.2 and §11.5. Everything a search engine
 * reads on `/[locale]/playground/[language]` is assembled here from the
 * language's bundled contract and its content module, so a test can pin it
 * without rendering a route: the title is absolute (the root layout's
 * `%s | Progsity` template would otherwise stack a second suffix on a title
 * that already names the product), the hreflang pair carries `x-default` like
 * the sitemap does, and the JSON-LD names the FAQ that actually renders.
 *
 * `{version}` is the one token the content modules may use. It is filled from
 * the contract's default runner version (`docs/24` §11.3: the version answer
 * comes from the table, never from prose), so a runtime bump on the backend
 * changes every page that mentions one without a copy edit.
 */
import type { Metadata } from "next";
import type { PlaygroundLanguage } from "../config/languages";
import type { LanguagePlaygroundContent } from "../content/types";
import { localeAlternates } from "@/lib/seo/locale-alternates";
import { bcp47, type JsonLdObject } from "@/lib/seo/json-ld";

export type ContentLocale = "en" | "bn";

export function contentLocale(locale: string): ContentLocale {
  return locale === "bn" ? "bn" : "en";
}

/** "Python 3.11", "C++17", "Node.js 22": the label of the runner's default version. */
export function defaultVersionLabel(language: PlaygroundLanguage): string {
  const id = language.defaultVersionId.runner;
  return language.versions.find((version) => version.id === id)?.label ?? id;
}

export function fillVersion(text: string, versionLabel: string): string {
  return text.replace(/\{version\}/g, versionLabel);
}

/**
 * The meta description: the language's own sentence when its content module
 * has one, the interpolated template otherwise. Either way the version token
 * is filled, and the result is one line.
 */
export function languagePageDescription(input: {
  locale: string;
  language: PlaygroundLanguage;
  content: LanguagePlaygroundContent | undefined;
  fallback: string;
}): string {
  const own = input.content?.seo?.description[contentLocale(input.locale)];
  const text = own && own.trim() ? own : input.fallback;
  return fillVersion(text, defaultVersionLabel(input.language)).replace(/\s+/g, " ").trim();
}

export function languagePageMetadata(input: {
  locale: string;
  slug: string;
  siteUrl: string;
  title: string;
  description: string;
}): Metadata {
  const path = `/playground/${input.slug}`;
  const alternates = localeAlternates(input.locale, path);
  return {
    title: { absolute: input.title },
    description: input.description,
    metadataBase: new URL(input.siteUrl),
    manifest: `/${input.locale}/manifest.webmanifest`,
    alternates,
    openGraph: {
      type: "website",
      title: input.title,
      description: input.description,
      url: alternates.canonical as string,
      siteName: "Progsity",
      locale: input.locale === "bn" ? "bn_BD" : "en_US",
    },
    twitter: {
      card: "summary_large_image",
      title: input.title,
      description: input.description,
    },
  };
}

/**
 * Three objects, not one blob: the tool (`SoftwareApplication`), the questions
 * on the page (`FAQPage`, answers with the version filled) and where the page
 * sits (`BreadcrumbList`). Each names the language the page is rendered in.
 */
export function languagePageJsonLd(input: {
  locale: string;
  siteUrl: string;
  language: PlaygroundLanguage;
  content: LanguagePlaygroundContent;
  description: string;
  homeLabel: string;
  playgroundLabel: string;
}): JsonLdObject[] {
  const base = input.siteUrl.replace(/\/$/, "");
  const loc = contentLocale(input.locale);
  const pageUrl = `${base}/${input.locale}/playground/${input.language.seoSlug}`;
  const inLanguage = bcp47(input.locale);
  const version = defaultVersionLabel(input.language);

  const application: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: `Progsity IDE: online ${input.language.name} compiler and playground`,
    applicationCategory: "DeveloperApplication",
    operatingSystem: "Web",
    inLanguage,
    description: input.description,
    url: pageUrl,
    softwareVersion: version,
    featureList: [
      `${version} on the Progsity runner`,
      "Run from the browser without an account",
      "Standard input panel",
      "Save, share and fork snippets",
      "Format in the browser",
    ],
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "Progsity", url: base },
  };

  const faq: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage,
    mainEntity: input.content.faq.map((item) => ({
      "@type": "Question",
      name: fillVersion(item.question[loc], version),
      acceptedAnswer: { "@type": "Answer", text: fillVersion(item.answer[loc], version) },
    })),
  };

  const breadcrumbs: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: input.homeLabel, item: `${base}/${input.locale}` },
      {
        "@type": "ListItem",
        position: 2,
        name: input.playgroundLabel,
        item: `${base}/${input.locale}/playground`,
      },
      { "@type": "ListItem", position: 3, name: input.language.name, item: pageUrl },
    ],
  };

  return [application, faq, breadcrumbs];
}
